import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  normalizePageTabValue,
  PAGE_TAB_VALUES,
  type PageTabValue,
} from '@constants/pageTabs';
import { ROUTES } from '@constants/routes';
import {
  appendPageTabParam,
  getIframeQueryParamsFromSearch,
} from '@utils/queryParams';
import { isPageTabAvailableForMarket } from '@utils/pageTabAvailability';
import './WidgetNav.less';

export interface NavItem {
  label: string;
  tab: PageTabValue;
}

/**
 * Six-item widget navigation.
 * Figma nodes: desktop 3124:3206 / 3131:1966, mobile 3154:4147 / 3158:5950.
 * The integration URL stays on /analysis; tab selects the rendered page.
 */
const NAV_ITEMS: NavItem[] = [
  { label: 'サマリ', tab: PAGE_TAB_VALUES.summary },
  { label: '業績推移', tab: PAGE_TAB_VALUES.performanceTrends },
  { label: '配当', tab: PAGE_TAB_VALUES.dividends },
  { label: '他社比較', tab: PAGE_TAB_VALUES.comparison },
  { label: '適時開示', tab: PAGE_TAB_VALUES.instructions },
  { label: '投資指標', tab: PAGE_TAB_VALUES.investmentIndicators },
];

const WidgetNav: React.FC = () => {
  const location = useLocation();
  const { tab: rawTab, mkt } = getIframeQueryParamsFromSearch(location.search);
  const currentQueryTab = normalizePageTabValue(rawTab);
  const activeTab = currentQueryTab ?? PAGE_TAB_VALUES.summary;

  return (
    <nav className="widget-nav" aria-label="メインメニュー">
      {NAV_ITEMS.map((item) => {
        const isActive = item.tab === activeTab;

        // Availability is a shared market/page policy (e.g. US Instructions is
        // unavailable). An unavailable item renders as a genuinely disabled
        // control: no href, no navigation, and no notification.
        if (!isPageTabAvailableForMarket(item.tab, mkt)) {
          return (
            <button
              key={item.label}
              type="button"
              className="widget-nav__item widget-nav__item--disabled"
              disabled
              aria-disabled="true"
            >
              {item.label}
            </button>
          );
        }

        const className = `widget-nav__item${isActive ? ' widget-nav__item--active' : ''}`;

        return (
          <Link
            key={item.label}
            to={{
              pathname: ROUTES.analysis,
              search: appendPageTabParam(location.search, item.tab),
            }}
            className={className}
            aria-current={isActive ? 'page' : undefined}
          >
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
};

export default WidgetNav;
